import React from 'react';

import { Link } from '@qality/common-components/src/atoms/Link';
import { Table } from '@qality/common-components/src/atoms/Table';
import { Text } from '@qality/common-components/src/atoms/Text';
import { formatDate } from '@qality/common-components/src/lib/util/dateTime';
import { stopPropagation } from '@qality/common-components/src/lib/util/event';
import { Issue } from '@qality/common-components/src/molecules/Issue';
import { TestCycle } from '@qality/common-model';
import { StorageOfUniqueServices } from '@qality/common-service';

import { ExecutionStatusSummary } from '../../execution-status-summary/ExecutionStatusSummary';

export interface TestCyclesOverviewTableViewProps {
  testCycle: TestCycle;
  showProject: boolean;
  showStatus: boolean;
  handleOverviewAction: (testCycleId) => void;
}

export const TestCyclesOverviewRowGadget: React.FC<TestCyclesOverviewTableViewProps> =
  ({
    testCycle,
    showProject,
    showStatus,
    handleOverviewAction,
  }: TestCyclesOverviewTableViewProps) => {
    const testCycleURL = StorageOfUniqueServices.get(
      'URLService'
    ).getTestCycleUrl(testCycle.id);
    const projectURL = StorageOfUniqueServices.get('URLService').getProjectUrl(
      testCycle.project.key
    );
    const versionURL = testCycle.version
      ? StorageOfUniqueServices.get('URLService').getVersionUrl(
          testCycle.project?.key,
          testCycle.version?.id
        )
      : '';

    return (
      <Table.Tr
        isClickable
        isHoverable
        data-testid='TestCyclesOverviewTable.Row-Tr'
        onClick={() => handleOverviewAction(testCycle.id)}
      >
        <Table.Td>
          <Table.ContentHelper flex='inline-flex' onClick={stopPropagation}>
            <Issue title={testCycle.name} issueLink={testCycleURL} addPadding />
          </Table.ContentHelper>
        </Table.Td>

        {showProject && (
          <Table.Td>
            <Table.ContentHelper flex='inline-flex' onClick={stopPropagation}>
              <Issue
                iconSrc={testCycle.project.iconUrl}
                title={testCycle.project.name}
                issueLink={projectURL}
              />
            </Table.ContentHelper>
          </Table.Td>
        )}

        <Table.Td>
          <Table.ContentHelper alignItems='center' onClick={stopPropagation}>
            {testCycle.version ? (
              <Issue
                title={testCycle.version.name}
                issueLink={versionURL}
                addPadding
              />
            ) : (
              <Text inactive>None</Text>
            )}
          </Table.ContentHelper>
        </Table.Td>

        {showStatus && (
          <Table.Td>
            <Table.ContentHelper alignItems='center'>
              <ExecutionStatusSummary statusSummary={testCycle.status} />
            </Table.ContentHelper>
          </Table.Td>
        )}

        <Table.Td>
          <Table.ContentHelper alignItems='center'>
            {testCycle.created ? (
              <Text>{formatDate(testCycle.created, 'longDateFormat')}</Text>
            ) : (
              <Text inactive>None</Text>
            )}
          </Table.ContentHelper>
        </Table.Td>
        <Table.Td>
          <Table.ContentHelper alignItems='center'>
            {testCycle.dueDate ? (
              <Text>{formatDate(testCycle.dueDate, 'longDateFormat')}</Text>
            ) : (
              <Text inactive>None</Text>
            )}
          </Table.ContentHelper>
        </Table.Td>
        <Table.Td>
          <Table.ContentHelper alignItems='center' onClick={stopPropagation}>
            <Link onClick={() => handleOverviewAction(testCycle.id)}>View</Link>
          </Table.ContentHelper>
        </Table.Td>
      </Table.Tr>
    );
  };
